import { Component, Host, h, Prop, Listen, Element, Method, Watch } from '@stencil/core';
import { createSortableDrag } from '../../utils/sortable-drag';
import { Debounce } from '../../utils/decorators/debounce/debounce.decorator';
import { DescendantsObserver } from '../../utils/descendants-observer';

/**
 * @slot - Slot to add as many <sy-accordion-item> as necessary.
 */

@Component({
  tag: 'sy-accordion',
  styleUrls: ['scss/index.scss', 'scss/_tokens.scss'],
  shadow: true,
})
export class Accordion {
  // Private addedAccordionItem: Array<HTMLSyAccordionItemElement> = [];
  private descendantsObserver: DescendantsObserver;

  @Element() el: HTMLSyAccordionElement;

  /**
   * If true, only one item will be expanded at time.
   */
  @Prop({ reflect: true }) singleExpanded = false;

  /**
   * Sets arrow icon to display on the start or end of the header.
   */
  @Prop({ reflect: true }) alignArrow: 'start' | 'end' = 'start';

  /**
   * Disable entire accordion.
   */
  @Prop({ reflect: true }) disabled = false;

  /**
   * If true, actives drag function.
   */
  @Prop({ reflect: true }) enableDrag = false;

  /**
   * SortableJS config.
   */
  @Prop() dragConfig: any;

  @Watch('disabled')
  disabledWatcher(): void {
    this.el.querySelectorAll('sy-accordion-item').forEach((item) => {
      if (this.disabled) {
        item.disable();
      } else {
        item.enable();
      }
    });
  }

  /**
   * Disables all animations from all items
   */
  @Prop() disableAnimations = false;
  @Watch('disableAnimations')
  disableAnimationsWatcher(): void {
    this.setChildrenDisableAnimations();
  }

  @Listen('syAccordionItemWillExpand')
  onSyAccordionItemWillExpand(e: CustomEvent<void>): void {
    const expandedItem = e.target as HTMLElement;
    this.el.querySelectorAll(':scope > sy-accordion-item').forEach((item: HTMLSyAccordionItemElement) => {
      if (this.singleExpanded && !item.isEqualNode(expandedItem)) {
        item.collapse();
      }
    });
    e.stopPropagation();
  }

  /**
   * Expand all items. Only works if single-expanded is false.
   * If instant is true, there won't have open animation in all items.
   */
  @Method()
  async expandAll(instant = false): Promise<void> {
    if (this.singleExpanded) {
      console.warn('Warning: Calling expandAll in a single-expanded sy-accordion.');
    }
    if (!this.disabled && !this.singleExpanded) {
      const promises: Promise<boolean>[] = [];
      this.el.querySelectorAll('sy-accordion-item').forEach((item) => promises.push(item.expand(instant)));
      await Promise.all(promises);
    }
  }

  /**
   * Collapse all items.
   * If instant is true, there won't have close animation in all items.
   */
  @Method()
  async collapseAll(instant = false): Promise<void> {
    const promises: Promise<boolean>[] = [];
    this.el.querySelectorAll('sy-accordion-item').forEach((item) => promises.push(item.collapse(instant)));
    await Promise.all(promises);
  }

  componentWillLoad() {
    this.descendantsObserver = new DescendantsObserver('sy-accordion-item', (array: Array<any>) => {
      this.setDisableAnimation(array);
    });

    this.descendantsObserver.observe(this.el, {
      childList: true,
    });
  }

  componentDidLoad() {
    const defaultOptions = {
      handle: '.sy-accordion-drag-icon',
    };

    createSortableDrag(this.el as any, {
      ...defaultOptions,
      ...this.dragConfig,
    });

    this.setChildrenDisableAnimations();
  }

  disconnectedCallback() {
    if (this.el.isConnected) return;

    this.descendantsObserver?.disconnect();
  }

  @Debounce(300)
  private setDisableAnimation(addedAccordionItem) {
    addedAccordionItem.forEach((accordionItem) => {
      if (accordionItem.isConnected) {
        accordionItem.disableAnimations = this.disableAnimations;
      }
    });
  }

  private setChildrenDisableAnimations() {
    this.el.querySelectorAll('sy-accordion-item').forEach((item) => {
      item.disableAnimations = this.disableAnimations;
    });
  }

  render() {
    return (
      <Host>
        <slot></slot>
      </Host>
    );
  }
}
